
import React from "react";
import ClubCard from "./ClubCard";
import { Club } from "@/hooks/useClubs";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface ClubsListProps {
  clubs: Club[];
  loading: boolean;
  joiningClubId: string | null;
  handleJoinClub: (clubId: string, isCurrentlyMember: boolean, clubName?: string) => Promise<void>;
  onCreateClick: () => void;
  searchApplied: boolean;
}

const ClubsList: React.FC<ClubsListProps> = ({ 
  clubs, 
  loading, 
  joiningClubId, 
  handleJoinClub,
  onCreateClick,
  searchApplied
}) => {
  if (loading) {
    return (
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="bg-muted h-48 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (clubs.length === 0) {
    return (
      <motion.div 
        className="text-center py-12 bg-gradient-to-br from-muted/30 to-muted/50 rounded-lg border border-border/50 backdrop-blur-sm"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-xl font-medium mb-2 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
            No clubs found
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchApplied 
              ? "Try adjusting your search or filter criteria" 
              : "Be the first to create a club!"}
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={onCreateClick}
              className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Plus size={16} className="mr-2" /> Create Club
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div 
        className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {clubs.map((club, index) => (
          <motion.div
            key={club.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            layout
          >
            <ClubCard
              club={club}
              onJoinClick={() => handleJoinClub(club.id, !!club.is_member, club.name)}
              isJoining={joiningClubId === club.id}
            />
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export default ClubsList;
